import * as Throttle from 'promise-parallel-throttle';

import {Presenter} from '../../../../Support/with_presenter';
import {
    ErrorResponse,
    loadingResponse,
    NetworkRequestStatus,
    NetworkResult,
    SuccessResponse,
} from '../../../../Network/NetworkResult';
import {SnippetService} from '../../../../Network/SnippetService';
import {StrokesService} from '../../../../Network/StrokesService';
import {DateRange, DateRangeNonNull} from '../../../../Domain/DateRange';
import {DownloadedIndoorWorkout} from '../../../../Models/DownloadedIndoorWorkout';
import {Snippet} from '../../../../Models/Snippet';
import {ReportService} from '../../../../Network/ReportService';
import {
    endOfMonth,
    endOfWeek,
    endOfYear,
    isSameMonth,
    isSameWeek,
    isSameYear,
    startOfMonth,
    startOfWeek,
    startOfYear,
} from 'date-fns';
import {SplitStrategy, Strategy} from '../../../../Business/calculateSplitTimes';
import {Fraction} from '../../../../Domain/Fraction';
import {Report} from '../../../../Models/Report';
import {ReportTemplate} from '../Trend/Models/ReportTemplate';

export interface ReportData {
    snippets: Snippet[];
    downloadedIndoorWorkouts: DownloadedIndoorWorkout[];
}

export class CompareReportPresenter implements Presenter {
    public snippetsResult: NetworkResult<Snippet[]> = loadingResponse('Finding snippets...');
    public splitStrategy: SplitStrategy = {
        strategy: Strategy.Fraction,
        fraction: Fraction.fromPercentage(100),
    };

    private selectedSnippetIds: number[] = [];

    public get selectedSnippets(): Snippet[] {
        if (this.snippetsResult.status !== 'success') {
            return [];
        }
        const snippets = this.snippetsResult.data;
        return this.selectedSnippetIds.map((id) => snippets.find((snippet) => snippet.id === id)!);
    }

    public isSaving = false;

    public name: string = '';
    public description: string = '';
    public template: ReportTemplate;

    public isLocked: boolean = false;

    public isEditingName = false;
    public deleting: boolean = false;

    constructor(
        private report: Report | null,
        private id: string | null,
        private snippetCollectionIds: number[],
        private userIds: number[],
        private crewIds: number[],
        private dateRange: DateRange | null,
        private snippetService: SnippetService,
        private reportService: ReportService,
    ) {
        if (report) {
            this.name = report.name;
            this.description = report.description;
            this.template = report.template;
        } else {
            this.name = 'report';
            this.description = '';
            const emptyTemplate: ReportTemplate = {trends: []};
            this.template = emptyTemplate;
        }
    }

    public async mount(): Promise<void> {
        this.snippetsResult = await this.snippetService.getFiltered(
            this.snippetCollectionIds,
            this.userIds,
            this.crewIds,
            this.dateRange,
        );

        if (this.snippetsResult.status === 'success') {
            const snippets = this.snippetsResult.data;
            const sortedSnippets = [...snippets].sort((a, b) => {
                return a.publicWorkout.executedAt.getTime() - b.publicWorkout.executedAt.getTime();
            });
            this.selectedSnippetIds = sortedSnippets.slice(-8).map((snippet) => snippet.id);
        }
    }

    public unmount(): void {
        /* Noop */
    }

    public onChangeSplitStrategy(splitStrategy: SplitStrategy): void {
        this.splitStrategy = splitStrategy;
    }

    public async onChangeSelectedSnippetIds(snippetIds: number[]): Promise<void> {
        if (this.snippetsResult.status !== 'success') {
            return;
        }
        this.selectedSnippetIds = snippetIds;
    }

    public startEditName() {
        this.isEditingName = true;
    }

    public stopEditName() {
        this.isEditingName = false;
    }

    public async updateName(name: string) {
        this.name = name;
    }

    public async submit(): Promise<void> {
        this.isEditingName = false;
        this.isSaving = true;
        if (!this.report) {
            return;
        }
        try {
            await this.reportService.update(this.report.id!, this.name, this.description, this.isLocked, this.template);
        } catch (e) {
            console.error(e);
        } finally {
            this.isSaving = false;
        }
    }

    private updateDescriptionTimer: number | null = null;

    public updateDescription(description: string) {
        this.description = description;

        if (this.updateDescriptionTimer) {
            clearTimeout(this.updateDescriptionTimer);
        }
        this.updateDescriptionTimer = window.setTimeout(async () => {
            await this.submit();
        }, 500);
    }

    public async delete(): Promise<void> {
        if (!this.id) {
            return;
        }
        try {
            this.deleting = true;
            await this.reportService.delete(this.id);
            window.location.href = '/reports';
        } finally {
            this.deleting = false;
        }
    }

    public async removeSnippet(snippetId: number): Promise<void> {
        try {
            this.deleting = true;
            await this.snippetService.delete(String(snippetId));
            if (this.snippetsResult.status === NetworkRequestStatus.Success) {
                //When things are successful, we can safely assume that the snippet is removed from the server
                this.snippetsResult = {
                    status: NetworkRequestStatus.Success,
                    data: 
                        this.snippetsResult.data.filter((snippet) => {
                            return '' + snippet.id !== '' + snippetId;
                        }),
                };
                this.selectedSnippetIds = this.selectedSnippetIds.filter((selectedSnippetId) => {
                        return '' + selectedSnippetId !== '' + snippetId;
                    })
                
            }
        } finally {
            this.deleting = false;
        }
    }
}
